import { CATEGORY, categoryDisplayNames, GROUP_STATUS, LEAD_TYPE } from '@brighte-enterprise/common/constants';
import { EnterpriseApplicationStatus } from '@prisma/client';

import { RecommendedVendorSelectionMode } from '@app-types/RecommendedVendorSelectionMode';
import { UserRole } from '@app-types/User';
import type { EnterprisePartner, PartnerConfigStepFlow, PreMarketplaceConfig } from '@typeDefs/groups';

import { notOnlyCategory, NURTURE_TOPICS_STEP_CONFIG, nurtureVendorSteps } from './nurture-flow';

// TODO(INT-1319): temporarily retained through the ET-1878 revert to back the
// EnterpriseApplication.cancellable resolver, which customer-application still reads.
// The packages/core copy and the cross-package sync test were removed; delete this too
// once the cancellable field goes with the companion customer-application revert.
export const applicationCancellableStatuses: EnterpriseApplicationStatus[] = [
  EnterpriseApplicationStatus.QUOTE,
  EnterpriseApplicationStatus.IN_PROGRESS,
];

export enum FINANCIAL_PRODUCT_SLUG {
  Electrify2515 = 'electrify-2515',
}

export const states: Record<string, string> = {
  ACT: 'Australian Capital Territory',
  NSW: 'New South Wales',
  NT: 'Northern Territory',
  QLD: 'Queensland',
  SA: 'South Australia',
  TAS: 'Tasmania',
  VIC: 'Victoria',
  WA: 'Western Australia',
};

export const showContactRoles = [UserRole.AdminOperations, UserRole.EnterpriseConcierge, UserRole.EnterprisePartnerPII];

export enum ALERT_LEVEL {
  Warning = 'warning',
  Critical = 'critical',
}

export const pipelineAlertThresholds: Record<ALERT_LEVEL, number> = {
  [ALERT_LEVEL.Critical]: 15,
  [ALERT_LEVEL.Warning]: 10,
};

export const newStatusStaleDays = 10;

export const allowedRolesForEnterprisePartners = [
  UserRole.Team,
  UserRole.Vendor,
  UserRole.Manager,
  UserRole.AdminOperations,
  UserRole.Admin,
  UserRole.SuperAdmin,
  UserRole.EnterpriseConcierge,
  UserRole.Service,
];

// Backward compatibility alias
export const allowedRolesForEnterpriseGroups = allowedRolesForEnterprisePartners;

export const allowedRolesForDocuments = [UserRole.SuperAdmin, UserRole.Admin, UserRole.AdminOperations];

export const VALID_FILE_EXTENSIONS = {
  IMAGES: ['jpg', 'jpeg', 'png'],
  DOCUMENTS: ['pdf'],
  SPREADSHEETS: ['csv'],
  ALL: ['jpg', 'jpeg', 'png', 'pdf', 'csv'],
};

export const internalRoles = [UserRole.SuperAdmin, UserRole.Admin, UserRole.AdminOperations];

// ── Shared pre-marketplace config fragments ───────────────────────────────────
// Used for both channel-level fields and backward-compat partner-level fields.

const SOLAR_BUNDLE_SUBCATEGORIES = {
  'solar-battery-ev-charger': ['solar-system', 'battery-storage', 'ev-charger'],
};

// INT-1294: energy categories that drive the commbank household/upgrade-profile branch.
// When the buyer's `categories` selection includes any of these, the `household` and
// `upgrade-profile` steps are shown; otherwise both are skipped. Sourced from CATEGORY so the
// branch stays in sync with the canonical category slugs.
const COMMBANK_ENERGY_CATEGORIES = [
  CATEGORY.SolarSystem,
  CATEGORY.SolarBatterySystem,
  CATEGORY.SolarEvCharger,
  CATEGORY.BatteryEvCharger,
  CATEGORY.BatteryStorage,
  CATEGORY.EvCharger,
  CATEGORY.SolarBatteryEvCharger,
];

const COMMBANK_STEP_FLOW: PartnerConfigStepFlow[] = [
  { id: 'introduction', required: true },
  { id: 'location', required: true },
  { id: 'categories', required: true },
  // Show `household` only when an energy category is selected; `in` matches when any selected
  // category is in the list. Steps without `visibleWhen` are always shown, so the current
  // (non-branching) consumer is unaffected.
  {
    id: 'household',
    required: true,
    visibleWhen: {
      match: 'all',
      clauses: [{ questionId: 'categories', operator: 'in', values: COMMBANK_ENERGY_CATEGORIES }],
    },
  },
  // `upgrade-profile` uses the SAME condition as `household`: shown when an energy category is
  // selected, hidden otherwise. Both steps appear together for energy-category buyers.
  {
    id: 'upgrade-profile',
    required: true,
    visibleWhen: {
      match: 'all',
      clauses: [{ questionId: 'categories', operator: 'in', values: COMMBANK_ENERGY_CATEGORIES }],
    },
  },
  { id: 'financing-interest', required: true },
  { id: 'intent', required: true },
  { id: 'recommended-vendors', required: true },
  // `required: true` means the step is shown; the step itself is skippable
  // (the customer can continue without uploading a bill, INT-1134).
  { id: 'bill-upload', required: true },
  { id: 'contact', required: true },
];

const NSW_HESL_STEP_FLOW: PartnerConfigStepFlow[] = [
  { id: 'eligibility-check', required: true },
  { id: 'introduction', required: true },
  { id: 'location', required: true },
  { id: 'categories', required: true },
  { id: 'bundle-categories', required: false },
  { id: 'recommended-vendors', required: true },
  { id: 'contact', required: true },
];

const STANDARD_BUNDLE_STEP_FLOW: PartnerConfigStepFlow[] = [
  { id: 'introduction', required: true },
  { id: 'location', required: true },
  { id: 'categories', required: true },
  { id: 'bundle-categories', required: false },
  { id: 'intent', required: true },
  { id: 'recommended-vendors', required: true },
  { id: 'contact', required: true },
];

const OVO_STEP_FLOW: PartnerConfigStepFlow[] = [
  { id: 'introduction', required: true },
  { id: 'location', required: true },
  { id: 'bundle-categories', required: true },
  { id: 'intent', required: true },
  { id: 'recommended-vendors', required: true },
  // No `bill-upload` here: the INT-1134 bill-upload step is releasing for
  // CommBank only for now (product call, 2026-06-30); OVO will be added later.
  { id: 'contact', required: true },
];

// Both IAG channels use the same pre-marketplace journey. Shared so that a new question or branch
// reaches both channels, which listing the configuration twice quietly failed to do.
const IAG_STEP_FLOW: PartnerConfigStepFlow[] = [
  { id: 'location', required: true },
  // No `categories` step: IAG launches with the MVP bundle only (INT-1302),
  // so it goes straight to `bundle-categories` like OVO.
  { id: 'bundle-categories', required: true },
  { id: 'household', required: true },
  { id: 'purchase-priority', required: true },
  { id: 'financing-interest', required: true },
  { id: 'intent', required: true },
  // IAG has no `categories` step, so the category answer it narrows the branch on lives on
  // `bundle-categories`. The education content is solar and battery material, so a buyer who only
  // wants a charger skips the branch and keeps the plain vendor flow above.
  ...nurtureVendorSteps({ alsoShowWhen: [notOnlyCategory('bundle-categories', CATEGORY.EvCharger)] }),
  { id: 'contact', required: true },
];

const iagPreMarketplace = (selectionMode: RecommendedVendorSelectionMode): PreMarketplaceConfig => ({
  stepFlow: IAG_STEP_FLOW,
  stepConfiguration: {
    bundleCategories: { enabled: true, subcategories: SOLAR_BUNDLE_SUBCATEGORIES },
    financingInterest: { version: 'brighte' },
    topics: NURTURE_TOPICS_STEP_CONFIG,
    // Partner-neutral content: the CBA version names CommBank finance and Cashback.
    education: { version: 'default' },
    recommendedVendors: {
      selectVendors: true,
      selectionMode,
      pickVendorsInMarketplace: false,
    },
  },
});

export const enterpriseGroups: EnterprisePartner[] = [
  {
    id: 'evee',
    name: 'Evee',
    type: LEAD_TYPE.Concierge,
    status: { staging: GROUP_STATUS.active, uat: GROUP_STATUS.active, prod: GROUP_STATUS.active },
    referralConfiguration: { referralSupportsMultipleCategories: false, packageSupportsMultipleVendors: false },
    configuration: {
      premiumLeads: false,
      intake: false,
    },
  },
  {
    id: 'tas',
    name: 'TAS Government',
    type: LEAD_TYPE.Marketplace,
    referralConfiguration: { referralSupportsMultipleCategories: false, packageSupportsMultipleVendors: false },
    configuration: {
      premiumLeads: false,
      intake: false,
    },
  },
  {
    id: '1mw',
    name: 'One Million Women',
    type: LEAD_TYPE.Concierge,
    status: { staging: GROUP_STATUS.active, uat: GROUP_STATUS.active, prod: GROUP_STATUS.inactive },
    referralConfiguration: { referralSupportsMultipleCategories: false, packageSupportsMultipleVendors: false },
    configuration: {
      premiumLeads: false,
      intake: false,
    },
  },
  {
    id: 'commbank',
    name: 'CommBank',
    type: LEAD_TYPE.Marketplace,
    status: { staging: GROUP_STATUS.active, uat: GROUP_STATUS.active, prod: GROUP_STATUS.active },
    referralConfiguration: { referralSupportsMultipleCategories: true, packageSupportsMultipleVendors: false },
    configuration: {
      // ── Channels ──────────────────────────────────────────────────────────
      channels: [
        {
          slug: 'commbank',
          financialProductId: 'commbank-residential',
          title: 'Home Energy Upgrades',
          showTitleInMarketplace: false,
          vendorSortingPreference: 'RANKING',
          preMarketplace: {
            stepFlow: COMMBANK_STEP_FLOW,
            stepConfiguration: {
              categories: { multiSelection: true, options: [] },
              financingInterest: { version: 'commbank' },
              recommendedVendors: {
                selectVendors: false,
                selectionMode: RecommendedVendorSelectionMode.AUTO_SELECTED,
                pickVendorsInMarketplace: true,
              },
            },
          },
          // Customer "what happens next" steps for the CBA journey — shown on the
          // success page, Tracker, and the request-received email (INT-1133, copy
          // signed off by Risk). `content` is the step summary and is required;
          // `items` is the optional checklist rendered beneath it.
          progressSteps: [
            {
              title: 'Prepare for your call with the vendors',
              content: 'Vendors can quote faster and more accurately if you have a few details ready.',
              items: [
                {
                  title: 'What to have ready',
                  subitems: [
                    'A recent electricity bill, showing your usage and tariff',
                    'Any shading over your roof from trees or other buildings',
                    'Whether you have existing solar panels or a battery',
                    'Your switchboard type or a photo, if it’s easy to get',
                  ],
                },
              ],
            },
            {
              title: 'Vendors will be in touch',
              content: 'They will contact you in the next couple of days to discuss your request.',
              items: [
                {
                  title: 'What to expect from the first call',
                  subitems: [
                    'Some vendors quote over the phone, others will book a site visit first',
                    'They will ask about your energy use and what you want from the upgrade',
                    'If you own the home with someone else, try to have them join the call',
                  ],
                },
              ],
            },
            {
              title: 'Choose your payment option online',
              content:
                "You'll have the option to apply for one of CommBank's financing solutions or you can pay with a debit or credit card.",
            },
            {
              title: 'Installation',
              content:
                'Once your application for finance has been approved, or you have arranged payment with your chosen vendor, they will schedule the installation with you.',
            },
            {
              title: 'Claim your Prezzee Smart eGift Card via Brighte',
              content:
                'Claim a Prezzee Smart eGift Card (up to $1,000) when you purchase your upgrade through Brighte’s marketplace and fund it using a CommBank home energy loan. You must complete both the post-approval and post-installation surveys.',
            },
          ],
          heroImage: 'hero-cba-request-b',
        },
      ],

      // ── Branding ──────────────────────────────────────────────────────────
      hasCoBrandedLogo: true,
      sparkThemeName: 'pantheon',
      backgroundColour: '#F4F4F4',
      cardBorderRadius: 'none',

      // ── Behaviour ─────────────────────────────────────────────────────────
      requireLead: true,
      allowLeadCreation: true,
      multiVendor: false,
      intake: false,
      premiumLeads: true,
      hasMarketingOptIn: false,
      hasPromotionContent: true,
      vendorBrowsingEnabled: true,
      filterVendorByPartner: false,
      redirectToSuccessPage: false,
      packageEnabled: true,
      pendingVendorFollowupEnabled: true,

      // ── Links & References ────────────────────────────────────────────────
      partnerWebsiteUrl: 'https://www.commbank.com.au',
      brighteSiteSlug: 'commbank',
      brighteSiteSubdomain: 'commbank',
      contactNumber: '1300 500 107',
      marketplaceFlag: 'commbank_residential',
      leadPricing: 35,
    },
  },
  {
    id: 'racq',
    name: 'RACQ',
    type: LEAD_TYPE.Marketplace,
    status: { staging: GROUP_STATUS.active, uat: GROUP_STATUS.active, prod: GROUP_STATUS.inactive },
    referralConfiguration: { referralSupportsMultipleCategories: true, packageSupportsMultipleVendors: false },
    configuration: {
      // ── Channels ──────────────────────────────────────────────────────────
      channels: [
        {
          slug: 'racq',
          financialProductId: 'brighte-pay',
          title: 'Home Upgrade Program',
          cmsContentSlug: 'racq',
          showTitleInMarketplace: true,
          vendorSortingPreference: 'RANKING',
          // Backfilled at channel level from the partner-level `customerIdRequirement` below
          // (INT-1410). Nothing has moved away yet: that field is deprecated but still populated,
          // so consumers that have not migrated keep working. It goes once they all read this.
          // The field is described entirely here — copy, mask, icon and checks — so a change to any
          // of it is a config change rather than a marketplace release.
          contactForm: {
            fields: [
              {
                name: 'partnerCustomerId',
                required: false,
                label: 'RACQ membership number',
                description: 'This is your 16-digit membership number',
                mask: '0000 0000 0000 0000',
                icon: 'racq-membership',
                // Format first so an obviously wrong number never reaches the RACQ API.
                validation: [
                  { type: 'pattern', pattern: '^\\d{16}$' },
                  { type: 'remote', provider: 'racq' },
                ],
              },
            ],
          },
          preMarketplace: {
            stepFlow: STANDARD_BUNDLE_STEP_FLOW,
            stepConfiguration: {
              categories: {
                multiSelection: false,
                options: ['solar-battery-ev-charger', 'hot-water-heat-pumps-hwhp', 'water-filtration'],
              },
              bundleCategories: { enabled: true, subcategories: SOLAR_BUNDLE_SUBCATEGORIES },
              recommendedVendors: {
                selectVendors: true,
                selectionMode: RecommendedVendorSelectionMode.CUSTOMER_SELECTION,
                pickVendorsInMarketplace: false,
              },
            },
          },
          // Copy locked to the consolidated lead-received email (INT-1147), template
          // brighte-emails src/emails/Customer/PackageEnabled/request-received.html —
          // keep in sync with any change there.
          progressSteps: [
            {
              title: 'Vendors will be in touch',
              content:
                'Once vendors receive your request, they will contact you in the next couple of days to discuss your request and book a site visit.',
            },
            {
              title: 'Review and accept quotes online',
              content:
                'Brighte will email you when your quotes are ready to review. You can select the quote package that best fits your needs and budget.',
            },
            {
              title: 'Choose your payment option online',
              content:
                "You'll have the option to apply for one of Brighte's financing solutions or you can pay with a debit or credit card.",
            },
            {
              title: 'Installation',
              content:
                'Once your application for finance has been approved, or you have arranged payment with your chosen vendor, they will schedule the installation with you.',
            },
          ],
          heroImage: 'racq-hero',
        },
      ],

      // ── Branding ──────────────────────────────────────────────────────────
      logoWidth: 90,
      sparkThemeName: 'bravo',
      backgroundColour: '#F1F4FF',
      cardBorderRadius: 'large',

      // ── Behaviour ─────────────────────────────────────────────────────────
      requireLead: false,
      allowLeadCreation: true,
      multiVendor: false,
      intake: false,
      premiumLeads: true,
      customerIdRequirement: 'optional',
      hasMarketingOptIn: true,
      hasCustomAssetPath: true,
      vendorBrowsingEnabled: false,
      filterVendorByPartner: true,
      redirectToSuccessPage: false,
      packageEnabled: true,

      // ── Links & References ────────────────────────────────────────────────
      brighteSiteSlug: 'racq',
      contactNumber: '1300 274 448',
      marketplaceFlag: 'marketplace_racq',
      // RACQ Solar leads RACQ listings, and is not badged as "RACQ Approved" — it is RACQ.
      // Two keys rather than one: they name the same vendor today, but featuring a different
      // vendor must not move the badge. Replaces a `tradingName` string match in
      // finance-core and marketplace (PLT-1528).
      featuredVendorSlugs: ['racq-solar'],
      ownVendorSlugs: ['racq-solar'],
    },
  },
  {
    id: 'ovo-energy',
    name: 'OVO Energy',
    type: LEAD_TYPE.Marketplace,
    status: { staging: GROUP_STATUS.active, uat: GROUP_STATUS.active, prod: GROUP_STATUS.active },
    referralConfiguration: { referralSupportsMultipleCategories: false, packageSupportsMultipleVendors: false },
    configuration: {
      // ── Channels ──────────────────────────────────────────────────────────
      channels: [
        {
          slug: 'ovo-energy',
          showTitleInMarketplace: false,
          vendorSortingPreference: 'RANKING',
          preMarketplace: {
            stepFlow: OVO_STEP_FLOW,
            stepConfiguration: {
              // No `categories` block — OVO_STEP_FLOW has no `categories` step (goes straight to `bundle-categories`).
              bundleCategories: { enabled: true, subcategories: SOLAR_BUNDLE_SUBCATEGORIES },
              recommendedVendors: {
                selectVendors: true,
                selectionMode: RecommendedVendorSelectionMode.CUSTOMER_SELECTION,
                pickVendorsInMarketplace: false,
              },
            },
          },
        },
      ],

      // ── Branding ──────────────────────────────────────────────────────────
      sparkThemeName: 'brighte',
      backgroundColour: '#F1F4FF',
      cardBorderRadius: 'large',

      // ── Behaviour ─────────────────────────────────────────────────────────
      requireLead: false,
      allowLeadCreation: true,
      multiVendor: false,
      intake: false,
      premiumLeads: true,
      hasMarketingOptIn: false,
      vendorBrowsingEnabled: true,
      filterVendorByPartner: true,
      redirectToSuccessPage: true,
      packageEnabled: false,

      // ── Links & References ────────────────────────────────────────────────
      contactNumber: '1300 274 448',
      marketplaceFlag: 'marketplace_ovo',
      leadPricing: 45,
    },
  },
  {
    id: 'iag',
    name: 'IAG',
    type: LEAD_TYPE.Marketplace,
    // Launched on prod per the IAG Marketplace Approach for Launch doc
    // (target go-live was 2026-08-03).
    status: { staging: GROUP_STATUS.active, uat: GROUP_STATUS.active, prod: GROUP_STATUS.active },
    referralConfiguration: { referralSupportsMultipleCategories: false, packageSupportsMultipleVendors: false },
    configuration: {
      // ── Channels ──────────────────────────────────────────────────────────
      channels: [
        {
          // IAG Staff Offer: internal link to a dedicated Brighte Landing Page.
          slug: 'iag',
          // No financialProductId: IAG wants vendors to offer whichever
          // finance product they're accredited for, and to skip the package
          // screen experience entirely.
          showTitleInMarketplace: false,
          vendorSortingPreference: 'RANKING',
          // The staff offer is for IAG employees, so the contact email doubles as proof of
          // employment — a rule on the form's existing email field, not a second address.
          contactForm: {
            fields: [
              {
                name: 'email',
                required: true,
                // No label or description: the form's own are right here. The staff-offer note
                // is additional, so it goes in infoMessage rather than replacing them.
                infoMessage: [
                  'Please provide your IAG email address to receive your employee discount. This will act as ' +
                    'your contact email address for your home energy upgrade',
                ],
                // The domain carries the marker, so "someone.iag@brighte.com.au" is rejected.
                // `.*` is explicit to satisfy the anchoring invariant every pattern is held to.
                validation: [{ type: 'pattern', pattern: '^.*@iag.*$', flags: 'i' }],
              },
            ],
          },
          preMarketplace: iagPreMarketplace(RecommendedVendorSelectionMode.FIXED_SELECTION_WITH_REPLACEMENT),
        },
        {
          // NRMA Rewards: tile embedded in the NRMA Insurance App.
          slug: 'nrma-rewards',
          showTitleInMarketplace: false,
          vendorSortingPreference: 'RANKING',
          hasChannelLogo: true,
          leadLabel: 'NRMA Rewards Lead',
          preMarketplace: iagPreMarketplace(RecommendedVendorSelectionMode.CUSTOMER_SELECTION),
        },
      ],

      // ── Branding ──────────────────────────────────────────────────────────
      sparkThemeName: 'brighte',
      backgroundColour: '#F1F4FF',
      cardBorderRadius: 'large',

      // ── Behaviour ─────────────────────────────────────────────────────────
      requireLead: false,
      allowLeadCreation: true,
      multiVendor: false,
      intake: false,
      premiumLeads: true,
      hasMarketingOptIn: false,
      vendorBrowsingEnabled: true,
      filterVendorByPartner: true,
      redirectToSuccessPage: true,
      packageEnabled: false,
      pendingVendorFollowupEnabled: true,

      // ── Links & References ────────────────────────────────────────────────
      contactNumber: '1300 274 448',
      marketplaceFlag: 'marketplace_iag',
      leadPricing: 35,
    },
  },
  {
    id: 'rewiring-au',
    name: 'Rewiring Australia',
    type: LEAD_TYPE.Marketplace,
    status: { staging: GROUP_STATUS.active, uat: GROUP_STATUS.active, prod: GROUP_STATUS.active },
    referralConfiguration: { referralSupportsMultipleCategories: true, packageSupportsMultipleVendors: true },
    configuration: {
      // ── Channels ──────────────────────────────────────────────────────────
      channels: [
        {
          slug: 'electrify-2515',
          financialProductId: 'electrify-2515',
          fixedPostcode: '2515',
          title: 'Electrify 2515 Community Pilot',
          showTitleInMarketplace: true,
          showNeedHelpPanel: true,
          supportDetails: {
            text: 'If you have any questions or would like support selecting a vendor, contact the Electrify 2515 Pilot Team.',
            linkText: 'Electrify 2515 Pilot Team',
            linkUrl: 'https://www.electrify2515.org/contact',
          },
          vendorSortingPreference: 'RANKING',
          leadLabel: 'Electrify 2515 Community Pilot',
          vendorGuidelineUrl: 'https://brighte.com.au/electrify-2515-vendor-guide',
          requiredCategories: [
            { slug: CATEGORY.SmartEnergyDevice, name: categoryDisplayNames[CATEGORY.SmartEnergyDevice] },
          ],
          // Copy locked to the consolidated lead-received email (INT-1147), template
          // brighte-emails src/emails/Customer/PackageEnabled/request-received.html —
          // keep in sync with any change there.
          progressSteps: [
            {
              title: 'Vendors will contact you',
              content: 'We have sent your request to the selected vendors.',
            },
            {
              title: 'Site visit and quoting',
              content: 'We recommend that vendors visit your home to provide you with the best solutions.',
            },
            {
              title: 'Application',
              content:
                'You will be able to choose your preferred vendors and equipment, and apply for subsidies and finance if you need it.',
            },
            {
              title: 'Installation',
              content: 'Your selected vendors will install your new equipment.',
            },
            {
              title: 'Payment',
              content:
                'After installation, your repayments will begin to Brighte or full payment is made to your selected vendor. Any subsidies will be deducted before payment.',
            },
          ],
          heroImage: 'electrify-2515-hero',
        },
      ],

      // ── Branding ──────────────────────────────────────────────────────────
      sparkThemeName: 'brighte',
      backgroundColour: '#F1F4FF',
      cardBorderRadius: 'large',

      // ── Behaviour ─────────────────────────────────────────────────────────
      requireLead: true,
      allowLeadCreation: false,
      multiVendor: true,
      intake: true,
      premiumLeads: false,
      hasMarketingOptIn: false,
      hasPromotionContent: true,
      vendorBrowsingEnabled: true,
      filterVendorByPartner: false,
      redirectToSuccessPage: false,
      packageEnabled: true,

      // ── Links & References ────────────────────────────────────────────────
      partnerWebsiteUrl: 'https://www.electrify2515.org/pilot',
      brighteSiteSlug: 'electrify-2515',
      contactNumber: '1300 135 100',
    },
  },
  {
    id: 'act',
    name: 'ACT Government',
    type: LEAD_TYPE.Marketplace,
    status: { staging: GROUP_STATUS.active, uat: GROUP_STATUS.active, prod: GROUP_STATUS.active },
    referralConfiguration: { referralSupportsMultipleCategories: false, packageSupportsMultipleVendors: false },
    configuration: {
      // ── Channels ──────────────────────────────────────────────────────────
      channels: [
        {
          slug: 'act',
          financialProductId: 'act-shs-loan',
          title: 'ACT Sustainable Household Scheme',
          showTitleInMarketplace: true,
          vendorSortingPreference: 'RANKING',
        },
        {
          slug: 'act-community',
          financialProductId: 'act-community-loan',
          title: 'ACT Community Household Scheme',
          showTitleInMarketplace: true,
          vendorSortingPreference: 'RANKING',
        },
        {
          slug: 'act-sfa',
          financialProductId: 'act-solar-for-apartments-loan',
          title: 'ACT Solar for Apartments',
          showTitleInMarketplace: true,
          vendorSortingPreference: 'RANKING',
        },
        // Product ID uses the original internal name (SLIP = Solar for Low Income Programme); renamed to HESP before launch but ID was not changed.
        {
          slug: 'act-hesp',
          financialProductId: 'act-slip-loan',
          title: 'ACT Home Energy Support Program',
          showTitleInMarketplace: true,
          vendorSortingPreference: 'RANKING',
        },
      ],

      // ── Branding ──────────────────────────────────────────────────────────
      sparkThemeName: 'brighte',
      backgroundColour: '#F1F4FF',
      cardBorderRadius: 'large',

      // ── Behaviour ─────────────────────────────────────────────────────────
      requireLead: false,
      allowLeadCreation: true,
      multiVendor: true,
      intake: false,
      premiumLeads: false,
      hasMarketingOptIn: false,
      hasPromotionContent: true,
      vendorBrowsingEnabled: true,
      filterVendorByPartner: false,
      redirectToSuccessPage: true,
      packageEnabled: false,
    },
  },
  {
    id: 'brighte',
    name: 'Brighte',
    type: LEAD_TYPE.Marketplace,
    status: { staging: GROUP_STATUS.active, uat: GROUP_STATUS.active, prod: GROUP_STATUS.active },
    referralConfiguration: { referralSupportsMultipleCategories: false, packageSupportsMultipleVendors: false },
    configuration: {
      // ── Channels ──────────────────────────────────────────────────────────
      channels: [
        {
          slug: 'brighte',
          showTitleInMarketplace: false,
          vendorSortingPreference: 'RANKING',
        },
      ],

      // ── Branding ──────────────────────────────────────────────────────────
      sparkThemeName: 'brighte',
      backgroundColour: '#F1F4FF',
      cardBorderRadius: 'large',

      // ── Behaviour ─────────────────────────────────────────────────────────
      requireLead: false,
      allowLeadCreation: true,
      multiVendor: false,
      intake: false,
      premiumLeads: false,
      hasMarketingOptIn: false,
      hasPromotionContent: true,
      vendorBrowsingEnabled: true,
      filterVendorByPartner: false,
      redirectToSuccessPage: true,
      packageEnabled: false,

      // ── Links & References ────────────────────────────────────────────────
      contactNumber: '1300 274 448',
      brighteSiteSlug: 'marketplace',
    },
  },
  {
    id: 'nsw-gov',
    name: 'NSW Government',
    type: LEAD_TYPE.Marketplace,
    status: { staging: GROUP_STATUS.active, uat: GROUP_STATUS.active, prod: GROUP_STATUS.active },
    referralConfiguration: { referralSupportsMultipleCategories: false, packageSupportsMultipleVendors: false },
    configuration: {
      // ── Channels ──────────────────────────────────────────────────────────
      channels: [
        {
          slug: 'nsw-home-energy-saver',
          financialProductId: 'brighte-home-energy-saver-loan',
          title: 'NSW Home Energy Saver Loan',
          showTitleInMarketplace: true,
          vendorSortingPreference: 'RANKING',
          showChannelLogo: false,
          leadLabel: 'Brighte Home Energy Saver Loan',
          vendorGuidelineUrl: 'https://brighte.com.au/nsw-vendors',
          vendorEmail: {
            leadReceivedPanel: {
              heading: 'About the loan',
              body: 'NSW Government’s Home Energy Saver Program is here — offering eligible households up to $15,000 at 0% interest for solar, batteries, heating and cooling, and more.',
              ctaLabel: 'Learn more',
            },
          },
          preMarketplace: {
            stepFlow: NSW_HESL_STEP_FLOW,
            stepConfiguration: {
              categories: {
                multiSelection: false,
                options: [
                  'solar-battery-system',
                  'hot-water-heat-pumps-hwhp',
                  'electric-heating-cooling-systems-reverse-cycle',
                  'ev-charging-infrastructure',
                  'double-glazing-windows',
                  'induction-cooktop',
                ],
              },
              bundleCategories: {
                enabled: true,
                subcategories: { 'solar-battery-system': ['solar-system', 'battery-storage'] },
              },
              recommendedVendors: {
                selectVendors: true,
                selectionMode: RecommendedVendorSelectionMode.CUSTOMER_SELECTION,
                pickVendorsInMarketplace: true,
              },
              introduction: {
                introductionType: 'BRIGHTE_INTRODUCTION',
                stepGuideLink: 'https://brighte.com.au/homeowners/nsw-home-energy-saver-loan/next-steps',
              },
              eligibilityCheck: {
                items: [
                  'Your installation address must be in NSW',
                  "Your household's total taxable income must be under $210,000",
                ],
              },
            },
          },
        },
      ],

      // ── Branding ──────────────────────────────────────────────────────────
      sparkThemeName: 'brighte',
      backgroundColour: '#F1F4FF',
      cardBorderRadius: 'large',

      // ── Behaviour ─────────────────────────────────────────────────────────
      requireLead: false,
      allowLeadCreation: true,
      multiVendor: false,
      intake: false,
      premiumLeads: false,
      hasMarketingOptIn: false,
      // No promotion content for nsw-gov: the Hygraph PromotionPartner enum has no
      // `nswgov` value (only act/brighte/commbank/rewiringau), so the marketplace
      // promotions query would 400. Keep this false to skip the promotions fetch
      // entirely (INT-1178).
      hasPromotionContent: false,
      vendorBrowsingEnabled: true,
      filterVendorByPartner: false,
      redirectToSuccessPage: true,
      packageEnabled: false,

      // ── Links & References ────────────────────────────────────────────────
      contactNumber: '1300 274 448',
    },
  },
];
